import React, { useEffect, useState } from 'react';
import { Card, Typography, Spin, Button } from 'antd';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import './SubscriptionPages.css';

const { Title, Text } = Typography;
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

const SubscriptionConfirmation = () => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'error'
  const [retryCount, setRetryCount] = useState(0);

  const checkStatusAndRedirect = async () => {
    // Wait for Clerk to fully load
    if (!isLoaded) {
      console.log('Clerk not loaded yet');
      return;
    }
    
    console.log('Clerk loaded. isSignedIn:', isSignedIn);
    
    if (!isSignedIn) {
      // If not signed in, redirect to sign-in page with return URL
      console.log('User not signed in, redirecting to sign-in');
      history.push('/sign-in?redirect_url=/subscription-confirmation');
      return;
    }

    try {
      console.log('Attempting to get token...');
      const token = await getToken({ template: "default" });
      console.log('Token received:', token ? 'Yes' : 'No');
      
      if (!token) {
        console.log('No token available, redirecting to sign-in');
        // If we can't get a token, redirect to sign-in
        history.push('/sign-in?redirect_url=/subscription-confirmation');
        return;
      }

      // Check subscription status
      console.log('Making API call to /subscription/status');
      const subscriptionRes = await fetch(`${BASE_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('API response status:', subscriptionRes.status);

      // If we get 401, the token is invalid
      if (subscriptionRes.status === 401) {
        console.log('401 Unauthorized - token invalid, redirecting to sign-in');
        history.push('/sign-in?redirect_url=/subscription-confirmation');
        return;
      }

      // If user still not found, retry (webhook might still be processing)
      if (subscriptionRes.status !== 200) {
        console.log('Non-200 response, retry count:', retryCount);
        if (retryCount < 10) { // Retry up to 10 times (30 seconds)
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 3000);
          return;
        } else {
          // Max retries reached, show error
          console.log('Max retries reached, showing error');
          setStatus('error');
          return;
        }
      }

      const subscriptionData = await subscriptionRes.json();
      const hasActiveSubscription = subscriptionData.status === 'active';

      if (!hasActiveSubscription) {
        // Subscription not active yet, keep checking
        if (retryCount < 10) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 3000);
          return;
        } else {
          setStatus('error');
          return;
        }
      }

      // Subscription is active, now check dealership status
      const dealershipRes = await fetch(`${BASE_URL}/user/dealership-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const dealershipData = await dealershipRes.json();
      const hasDealership = dealershipData.has_dealership;

      setStatus('success');

      // Wait a moment to show success, then redirect
      setTimeout(() => {
        if (hasDealership) {
          history.push('/dashboard');
        } else {
          history.push('/onboarding?step=signup');
        }
      }, 2000);

    } catch (err) {
      console.error("Error checking status:", err);
      setStatus('error');
    }
  };

  useEffect(() => {
    checkStatusAndRedirect();
  }, [retryCount, isLoaded, isSignedIn]);

  return (
    <div className="subscription-confirmation-container">
      <Card className="confirmation-card">
        {(!isLoaded || status === 'checking') && (
          <>
            <Spin 
              indicator={<LoadingOutlined className="confirmation-spinner" spin />} 
              style={{ display: 'block', marginBottom: 24 }}
            />
            <Title level={2} className="confirmation-title">
              {!isLoaded ? 'Loading...' : 'Confirming Your Subscription'}
            </Title>
            <Text className="confirmation-text">
              {!isLoaded 
                ? 'Initializing your session...' 
                : 'We\'re setting up your account and confirming your subscription...'
              }
            </Text>
            <Text className="subscription-text-secondary">
              This may take a few moments. Please don't close this page.
            </Text>
            {retryCount > 0 && (
              <Text className="retry-count-text">
                Attempt {retryCount + 1} of 10
              </Text>
            )}
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlined 
              style={{ 
                fontSize: 48, 
                color: '#52c41a', 
                display: 'block', 
                marginBottom: 24 
              }} 
            />
            <Title level={2} className="confirmation-success-title">
              Subscription Confirmed!
            </Title>
            <Text className="subscription-text">
              Welcome to Hooptie! Redirecting you to complete your setup...
            </Text>
          </>
        )}

        {status === 'error' && (
          <>
            <Title level={2} className="confirmation-error-title">
              Something went wrong
            </Title>
            <Text className="confirmation-text">
              We're having trouble confirming your subscription. Please try refreshing the page or contact support.
            </Text>
            <Button 
              type="primary" 
              size="large"
              className="confirmation-retry-button"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionConfirmation;